import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useEffect, useState, useRef } from "react";
import { ChevronRight, Volume2, VolumeX } from "lucide-react";

export default function Hero({ content }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;

    const timer = setTimeout(() => {
      setIsLoaded(true);
      setIsContentVisible(true);
    }, 100);

    if (video) {
      video.currentTime = 32;

      setTimeout(() => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log("Autoplay started successfully");
            })
            .catch((error) => {
              console.error("Autoplay failed:", error);
            });
        }
      }, 200);
    }

    return () => clearTimeout(timer);
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="home-hero relative min-h-screen flex items-center justify-center overflow-hidden pt-16 section-dark bg-musicBlack">
      {/* Video background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-musicBlack/70 via-musicBlack/50 to-musicBlack z-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-32 md:h-20 bg-gradient-to-t from-musicBlack via-musicBlack/95 to-musicBlack/40 z-20"></div>
        <video
          ref={videoRef}
          playsInline
          muted={isMuted}
          loop
          className="w-full h-full object-cover transition-opacity duration-1000 filter contrast-[1.1] brightness-[0.85]"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: "scale(1.2) translateY(-10%)",
            objectPosition: "center 30%",
            height: "110%",
            minHeight: "110vh",
          }}
        >
          <source
            //src="https://music-nation.beeinteractivemedia.com/wp-content/uploads/2025/05/WhatsApp-Video-2025-05-07-at-23.18.54_babed25f.mp4"
            src={content.video}
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>

        {/*<button
          onClick={toggleMute}
          className="absolute bottom-6 right-6 z-20 bg-black/60 hover:bg-black/80 text-white rounded-full p-2.5 transition-all duration-300"
          aria-label={isMuted ? "Unmute" : "Mute"}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>*/}
      </div>

      {/* Animated light blobs */}
      <div className="light-blob light-blob--1"></div>
      <div className="light-blob light-blob--2"></div>
      <div className="light-blob light-blob--3"></div>

      {/* Noise texture overlay */}
      <div className="noise-overlay"></div>

      {/* Content */}
      <div
        className={`relative z-10 editorial-container text-center transition-all duration-1000 ease-out ${
          isContentVisible
            ? "opacity-100 transform-none"
            : "opacity-0 translate-y-8"
        }`}
      >
        <h1 className="font-serif text-4xl md:text-7xl font-bold mb-6">
          <span
            className="block transform transition-transform duration-1000"
            style={{
              transform: isContentVisible
                ? "translateY(0)"
                : "translateY(30px)",
            }}
          >
            {content.title}
          </span>
          <span
            className="block transform transition-all duration-1000 delay-100 gradient-text-red relative"
            style={{
              transform: isContentVisible
                ? "translateY(0)"
                : "translateY(30px)",
              letterSpacing: "0.02em",
            }}
          >
            {content.titleHighlighted}
            <span
              className="absolute bottom-0 left-0 w-0 bg-gradient-to-r from-musicRed to-musicRedDark h-[3px]"
              style={{
                animation: isContentVisible
                  ? "expand-line 1.2s cubic-bezier(0.19, 1, 0.22, 1) forwards 0.8s"
                  : "none",
                boxShadow: "0 0 10px rgba(255, 59, 59, 0.3)",
              }}
            ></span>
          </span>
        </h1>

        <p
          className="font-sans max-w-3xl mx-auto mb-12 transform transition-all duration-1000 delay-300 text-musicWhite/90 text-lg md:text-xl"
          style={{
            transform: isContentVisible ? "translateY(0)" : "translateY(30px)",
            opacity: isContentVisible ? 1 : 0,
          }}
        >
          {content.subtitle}
          {!content.subtitle && (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  'We are built for <span className="text-musicWhite font-medium">creators</span> and <span className="text-musicWhite font-medium">businesses</span>. We Are Music Nation.',
              }}
            />
          )}
        </p>

        <div
          className="flex flex-col md:flex-row justify-center gap-4 transform transition-all duration-1000 delay-500"
          style={{
            transform: isContentVisible ? "translateY(0)" : "translateY(30px)",
            opacity: isContentVisible ? 1 : 0,
          }}
        >
          <Link to="/creators" className="group">
            <Button className="cta-button w-full md:w-auto" size="lg">
              <span className="flex items-center font-sans">
                I’m a Creator / Rights Holder
                <ChevronRight
                  className="ml-1 transition-transform duration-300 group-hover:translate-x-1"
                  size={18}
                />
              </span>
            </Button>
          </Link>
          <Link to="/businesses" className="group">
            <Button
              variant="outline"
              className="hero-business-btn secondary-button w-full md:w-auto"
              size="lg"
            >
              <span className="flex items-center text-musicWhite font-sans">
                I am a Business that uses Music
                <ChevronRight
                  className="ml-1 transition-transform duration-300 group-hover:translate-x-1"
                  size={18}
                />
              </span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Angular section divider */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden z-10">
        <svg
          className="w-full"
          style={{ height: "60px", display: "block" }}
          viewBox="0 0 1200 60"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          fill="#222222"
        >
          <polygon points="0,60 1200,60 1200,0 600,60 0,0" />
        </svg>
      </div>
    </section>
  );
}
